import React from 'react';
import { Slide, CoverRoster, CoverJoin } from './stops.jsx';
import { pyRepr } from '../lib/handPose.js';
import './tagStops.css';

/* The teaching slides of PYVAR2, drawn in theme A. One idea per slide; the
   picture carries it and the words label the picture. A value is always a
   chip coloured by its type (int blue, float orange); a name is always an
   uncoloured paper tag, because the type belongs to the value. */

/** A name tag. */
export function Tag({ name, struck = false, absent = false, className = '' }) {
  return (
    <span className={`nt ${struck ? 'is-struck' : ''} ${absent ? 'is-absent' : ''} ${className}`}>
      <span>{name}</span>
    </span>
  );
}

/** A value chip, coloured by its type, printed the way Python prints it. */
export function Val({ value, kind, className = '' }) {
  const text = pyRepr(value, kind);
  const dot = kind === 'float' ? text.indexOf('.') : -1;
  return (
    <span className={`val is-${kind} ${className}`}>
      {dot < 0 ? text : (
        <>
          {text.slice(0, dot)}
          <span className="dot">.</span>
          {text.slice(dot + 1)}
        </>
      )}
    </span>
  );
}

/* ------------------------------------------------------------- name-tag
   score = 25 ties the tag to 25; score = 30 moves the tag to 30 and leaves
   25 behind. The slide is the tag moving. */
export function NameTagStop({ stop, substep = 0 }) {
  const at = Math.min(substep, stop.binds.length - 1);
  return (
    <Slide kicker={stop.kicker} title={stop.title} className="tg-nametag">
      <div className="tg-nametag-body">
        <div className="code-panel tg-code">
          {stop.binds.map((b, i) => (
            <div key={i} className={`tg-code-line ${i > at ? 'is-hidden' : ''} ${i === at ? 'is-now' : ''}`}>
              {b.code}
            </div>
          ))}
        </div>

        <div className="tg-bind-stage" style={{ '--at': at }} aria-hidden="true">
          {stop.binds.map((b, i) => (
            <div key={i} className={`tg-bind-row ${i > at ? 'is-hidden' : ''} ${i < at ? 'is-old' : ''}`}>
              <Val value={b.value} kind={b.kind} />
            </div>
          ))}
          <div className="tg-bind-tag">
            <Tag name={stop.name} />
            <span className="tg-bind-string" />
          </div>
        </div>
      </div>

      <div className={`tg-rule ${substep >= stop.binds.length ? '' : 'is-hidden'}`}>
        <p className="tg-rule-head">{stop.rule}</p>
        <div className="tg-rule-eg">
          <span className="code-panel tg-rule-code">{stop.example.code}</span>
          <span className="tg-rule-note">{stop.example.note}</span>
        </div>
      </div>
    </Slide>
  );
}

/* ----------------------------------------------------------- name-rules
   Three rules, each with a name that breaks it, what Python answers, and
   the name fixed. None of these names is one the tag-pick question uses. */
export function NameRulesStop({ stop }) {
  return (
    <Slide kicker={stop.kicker} title={stop.title} className="tg-rules">
      <div className="tg-rules-grid">
        {stop.rules.map((r, i) => (
          <div key={i} className="tg-rule-card">
            <h2 className="tg-rule-card-head">{r.head}</h2>
            <div className="code-panel tg-rule-card-code">
              <span>{r.bad}</span>
              {'\n'}
              <span className="out">{r.error}</span>
            </div>
            <div className="tg-rule-card-fix">
              <span className="tg-fix-label">แก้เป็น</span>
              <Tag name={r.fix} />
              <b className="tg-tick" aria-label="ใช้ได้">✓</b>
            </div>
          </div>
        ))}
      </div>
    </Slide>
  );
}

/* -------------------------------------------------------- count-measure
   The answer to the hook's question: fingers are counted, so whole; the
   gap is measured, so it always has a fraction. */
function Counted({ n }) {
  return (
    <div className="tg-counted" aria-hidden="true">
      {Array.from({ length: n }).map((_, i) => (
        <span key={i} className="tg-counted-unit">{i + 1}</span>
      ))}
    </div>
  );
}

function Ruler({ value, max = 6 }) {
  const ticks = Array.from({ length: max * 2 + 1 });
  const x = (v) => 20 + (v / max) * 560;
  return (
    <svg className="tg-ruler" viewBox="0 0 600 120" aria-hidden="true">
      <rect x="2" y="40" width="596" height="76" rx="6" fill="#fff" stroke="currentColor" strokeWidth="4" />
      {ticks.map((_, i) => (
        <line key={i} x1={x(i / 2)} x2={x(i / 2)} y1="40" y2={i % 2 ? 66 : 84} stroke="currentColor" strokeWidth={i % 2 ? 2.5 : 4} />
      ))}
      {Array.from({ length: max + 1 }).map((_, i) => (
        <text key={i} x={x(i)} y="108" textAnchor="middle" className="tg-ruler-num">{i}</text>
      ))}
      <path d={`M ${x(value) - 12} 6 h 24 l -12 24 z`} className="tg-ruler-mark" />
      <line x1={x(value)} x2={x(value)} y1="28" y2="116" className="tg-ruler-markline" />
    </svg>
  );
}

export function CountMeasureStop({ stop }) {
  const { count, measure } = stop;
  return (
    <Slide kicker={stop.kicker} title={stop.title} className="tg-cm">
      <div className="tg-cm-halves">
        <div className="tg-cm-half">
          <Counted n={count.value} />
          <p className="tg-cm-label">{count.label}</p>
          <div className="tg-chip">
            <Tag name={count.name} /> <Val value={count.value} kind="int" />
            <span className="type-word is-int">int</span>
          </div>
        </div>
        <div className="tg-cm-half">
          <Ruler value={measure.value} />
          <p className="tg-cm-label">{measure.label}</p>
          <div className="tg-chip">
            <Tag name={measure.name} /> <Val value={measure.value} kind="float" />
            <span className="type-word is-float">float</span>
          </div>
        </div>
      </div>
    </Slide>
  );
}

/* --------------------------------------------------------------- type-of
   type() asks the value, so 5 and 5.0 differ; and one name can point at an
   int, then at a float — the type went with the value. */
export function TypeOfStop({ stop, substep = 0 }) {
  return (
    <Slide kicker={stop.kicker} title={stop.title} className="tg-typeof">
      <div className="tg-typeof-body">
        <div className="tg-typeof-left">
          <div className="code-panel tg-typeof-code">
            {stop.checks.map((c, i) => (
              <React.Fragment key={i}>
                <span>{'>>> '}type(<span className={`is-${c.kind}`}>{c.literal}</span>)</span>
                {'\n'}
                <span className="out">{`<class '${c.kind}'>`}</span>
                {i < stop.checks.length - 1 ? '\n' : ''}
              </React.Fragment>
            ))}
          </div>
          <p className="tg-typeof-note">{stop.note}</p>
        </div>

        <div className={`tg-typeof-right ${substep >= 1 ? '' : 'is-hidden'}`}>
          <div className="tg-bind-stage tg-typeof-stage" style={{ '--at': substep >= 1 ? 1 : 0 }} aria-hidden="true">
            {stop.moves.map((m, i) => (
              <div key={i} className={`tg-bind-row ${i === 0 && substep >= 1 ? 'is-old' : ''}`}>
                <Val value={m.value} kind={m.kind} />
                <span className="tg-typeof-said">
                  type({stop.name}) <span className="out">→</span> <span className={`type-word is-${m.kind}`}>{m.kind}</span>
                </span>
              </div>
            ))}
            <div className="tg-bind-tag">
              <Tag name={stop.name} />
              <span className="tg-bind-string" />
            </div>
          </div>
        </div>
      </div>
    </Slide>
  );
}

/* ----------------------------------------------------------------- cover
   The way in is a name tag on a lanyard: it swings once when the slide opens
   and hangs still. The roster underneath is the tags already handed out. */
export function TagCoverStop({ deck, room, onOpenQr, preview = false }) {
  const meta = deck.meta;
  return (
    <section className="stop tg-cover">
      <div className="tg-cover-left">
        <div className="tg-cover-head">
          <span className="tg-cover-unit">{meta.unit}</span>
          <h1 className="tg-cover-title">{meta.title}</h1>
          {meta.subtitle && <p className="tg-cover-sub">{meta.subtitle}</p>}
          {meta.instructor && <p className="tg-cover-teacher">{meta.instructor}</p>}
        </div>
        <CoverRoster room={room} preview={preview} />
      </div>
      <div className="tg-cover-hang">
        <div className="tg-cover-swing">
          <svg className="tg-cover-strap" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            <line x1="5" y1="0" x2="50" y2="100" />
            <line x1="95" y1="0" x2="50" y2="100" />
          </svg>
          <CoverJoin room={room} onOpenQr={onOpenQr} />
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------------- summary */
export function SummaryStop({ stop }) {
  return (
    <Slide kicker={stop.kicker} title={stop.title} className="tg-summary">
      <ol className="tg-summary-list">
        <li>
          <span className="tg-summary-pic" aria-hidden="true">
            <Tag name="score" /><span className="tg-mini-string" /><Val value={25} kind="int" />
          </span>
          <span className="tg-summary-text">{stop.lines[0]}</span>
        </li>
        <li>
          <span className="tg-summary-pic" aria-hidden="true">
            <Tag name="2score" struck /><Tag name="score2" />
          </span>
          <span className="tg-summary-text">{stop.lines[1]}</span>
        </li>
        <li>
          <span className="tg-summary-pic" aria-hidden="true">
            <Val value={3} kind="int" /><Val value={4.73} kind="float" />
          </span>
          <span className="tg-summary-text">{stop.lines[2]}</span>
        </li>
      </ol>
    </Slide>
  );
}
